import { PROPERTY_TABLE_COLUMNS } from './constants';

/**
 * Builder “property” tab grid (metadata + legacy FetchBlockOutputs tables).
 * Kept separate from `TableHelpersOpz` so dimensional/data grids stay isolated
 * and this module can evolve (incremental row rebuilds, saving overlays, etc.).
 */

function propertyTableHeaderRow(tableProperty: any) {
  const lastPropertyColumnIndex = (tableProperty?.length ?? 0) - 1;
  return {
    rowId: 'header',
    height: 40,
    cells: [
      ...(tableProperty?.length
        ? tableProperty.map((data: any, index: any) => {
            const row = {
              type: 'header',
              colType: 'header',
              nonEditable: true,
              text: data?.name,
              className: `header-cell ${index === 0 ? ' first-column ' : ''} ${
                index === lastPropertyColumnIndex ? ' last-index ' : ''
              }`,
            };
            return row;
          })
        : []),
      {
        type: 'header',
        text: 'Action',
        nonEditable: true,
        colType: 'header',
        className: 'header-cell header-last',
      },
    ],
  };
}

function propertyTableFooterRow(tableProperty: any) {
  const lastPropertyColumnIndex = (tableProperty?.length ?? 0) - 1;
  return {
    rowId: 'footer',
    height: 40,
    cells: [
      ...(tableProperty?.length
        ? tableProperty.map((data: any, index: number) => {
            if (index === 0) {
              const row = {
                type: 'addRow',
                text: '',
                showText: true,
                className: 'footer footer-first first-column',
                nonEditable: true,
              };
              return row;
            }
            const row = {
              type: 'text',
              text: '',
              nonEditable: true,
              className: `${index === lastPropertyColumnIndex ? ' last-index ' : ''} footer`,
              hasFormulaError: false,
            };
            return row;
          })
        : []),
      {
        type: 'text',
        text: '',
        nonEditable: true,
        className: 'footer footer-last',
        hasFormulaError: false,
      },
    ],
  };
}

export function buildPropertyTableItemRow(
  item: any,
  tableProperty: any,
  indicators: any,
  tableItemsLength: number,
  index: number,
) {
  const lastPropertyColumnIndex = (tableProperty?.length ?? 0) - 1;
  return {
    rowId: item?.id,
    height: 40,
    itemId: item?.id,
    itemName: item?.['Block indicators'],
    reorderable: true,
    isNewCell: !!item?.isNewCell,
    cells: [
      ...(tableProperty?.length
        ? tableProperty.map((data: any, index_: any) => {
            if (data?.name === 'Type') {
              const row = {
                hasFormulaError: item?.warning?.length > 0,
                hasFormulaErrorMessage: item?.warning,
                className: `${index_ === lastPropertyColumnIndex ? ' last-index ' : ''} ${
                  item?.warning?.length > 0 ? 'formula-error ' : ''
                }`,
                type: 'dropdown',
                nonEditable: !item?.id,
                selectOption: false,
                key: 'type',
                selectedValue: item[`${data?.name}`].toLowerCase(),
                values: [
                  {
                    label: 'Input',
                    value: 'input',
                  },
                  {
                    label: 'Calculation',
                    value: 'calculation',
                  },
                ],
                index,
                operation: '',
              };
              return row;
            }
            if (data?.name === 'Format') {
              const row = {
                className: `${index_ === lastPropertyColumnIndex && ' last-index '} ${
                  item?.warning?.length > 0 && 'formula-error '
                }`,
                type: 'dropdown',
                nonEditable: !item?.id,
                key: 'data_format',
                selectOption: false,
                selectedValue: item[`${data?.name}`].toLowerCase(),
                values: [
                  {
                    label: 'Number',
                    value: 'number',
                  },
                  {
                    label: 'Integer',
                    value: 'integer',
                  },
                  {
                    label: 'Percentage',
                    value: 'percentage',
                  },
                  {
                    label: 'Currency',
                    value: 'currency',
                  },
                ],
                index,
                operation: '',
              };
              return row;
            }
            if (data?.name === 'Roll up type') {
              const row = {
                className: `${index_ === lastPropertyColumnIndex ? ' last-index ' : ''} ${
                  item?.warning?.length > 0 ? 'formula-error ' : ''
                }`,
                type: 'dropdown',
                nonEditable: !item?.id,
                selectOption: false,
                key: 'data_type',
                selectedValue: item[`${data?.name}`].toLowerCase(),
                values: [
                  {
                    label: 'Number',
                    value: 'number',
                  },
                  {
                    label: 'Statistic',
                    value: 'statistic',
                  },
                  {
                    label: 'Percentage',
                    value: 'percentage',
                  },
                  {
                    label: 'Ratio',
                    value: 'ratio',
                  },
                  {
                    label: 'Balance',
                    value: 'balance',
                  },
                  {
                    label: 'Opening Balance',
                    value: 'opening_Balance',
                  },
                ],
                index,
                operation: '',
              };
              return row;
            }
            if (data?.name === 'Default chart type') {
              const row = {
                className: `${index_ === lastPropertyColumnIndex ? ' last-index ' : ''} ${
                  item?.warning?.length > 0 ? 'formula-error ' : ''
                }`,
                type: 'dropdown',
                nonEditable: !item?.id,
                selectOption: false,
                key: 'chart_type',
                selectedValue: item[`${data?.name}`].toLowerCase(),
                values: [
                  {
                    label: 'Line',
                    value: 'line',
                  },
                  {
                    label: 'Stack',
                    value: 'stack',
                  },
                  {
                    label: 'Bar',
                    value: 'bar',
                  },
                ],
                index,
                operation: '',
              };
              return row;
            }
            if (data?.name === 'Display as output') {
              const row = {
                type: 'checkbox',
                key: 'favourite',
                className: `${index_ === lastPropertyColumnIndex ? ' last-index ' : ''} ${
                  item?.warning?.length > 0 ? 'formula-error ' : ''
                }`,
                checked: item[`${data?.name}`],
              };
              return row;
            }
            if (data?.name === 'Display as sub-total') {
              const row = {
                type: 'checkbox',
                key: 'output_format',
                className: `${index_ === lastPropertyColumnIndex ? ' last-index ' : ''}${
                  item?.warning?.length > 0 ? 'formula-error ' : ''
                }`,
                extraKey: item[`${data?.name}`],
                checked: item[`${data?.name}`] === 'subtotal',
              };
              return row;
            }
            if (data?.name === 'Block indicators') {
              const row = {
                type: 'text',
                rows: tableItemsLength,
                indicatorType: item?.indicator_type,
                key:
                  data?.name === 'Block indicators'
                    ? 'name'
                    : data?.name === 'Formula'
                    ? 'formula'
                    : data?.name === 'Help text'
                    ? 'help'
                    : '',
                text: `${item[data?.name]}`,
                isDraggable: true,
                property: 'name',
                isNewCell: !!item?.isNewCell,
                index,
                nonEditable: !item?.id && !item?.indicatorId,
                hasFormulaError: item?.warning?.length > 0,
                hasFormulaErrorMessage: item?.warning,
                className: `first-column ${
                  index_ === lastPropertyColumnIndex ? ' last-index ' : ''
                } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
              };
              return row;
            }
            if (data?.name === 'Formula') {
              const row = {
                type: 'formula',
                text: `${item[data?.name]}`,
                className: `${
                  index_ === lastPropertyColumnIndex ? ' last-index ' : 'disabled cell-disabled'
                } ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
                options: indicators,
                nonEditable: true,
              };
              return row;
            }
            const row = {
              type: 'text',
              rows: tableItemsLength,
              key:
                data?.name === 'Block indicators'
                  ? 'name'
                  : data?.name === 'Formula'
                  ? 'formula'
                  : data?.name === 'Help text'
                  ? 'help'
                  : '',
              text: `${item[data?.name]}`,
              index,
              data,
              nonEditable: !item?.id && !item?.indicatorId,
              className: `${index_ === lastPropertyColumnIndex ? ' last-index ' : ''} ${
                item?.warning?.length > 0 ? 'formula-error ' : ''
              }`,
              hasFormulaError: false,
            };
            return row;
          })
        : []),
      {
        type: 'dropdown',
        buttonType: 1,
        selectOption: false,
        id: item?.id,
        className: `delete-column ${item?.warning?.length > 0 ? 'formula-error ' : ''}`,
        itemName: item?.['Block indicators'],
        nonEditable: !item?.id && !item?.indicatorId,
        values: [{ label: 'Delete row', value: '' }],
      },
    ],
  };
}

export function getPropertyTableRows(
  tableItemsProperty: any,
  tableProperty: any,
  indicators: any,
  options?: { includeFooterRow?: boolean },
) {
  const includeFooterRow = options?.includeFooterRow ?? true;
  return [
    propertyTableHeaderRow(tableProperty),
    ...(tableItemsProperty?.length
      ? tableItemsProperty.map((item: any, rowIndex: any) =>
          buildPropertyTableItemRow(
            item,
            tableProperty,
            indicators,
            tableItemsProperty.length,
            rowIndex,
          ),
        )
      : []),
    ...(includeFooterRow ? [propertyTableFooterRow(tableProperty)] : []),
  ];
}

const SAVING_CLASS = 'property-table-cell-saving';

function columnIdForCellIndex(cellIndex: number): string {
  if (cellIndex < PROPERTY_TABLE_COLUMNS.length)
    return String(PROPERTY_TABLE_COLUMNS[cellIndex].id);
  return 'delete';
}

/**
 * Non-destructive overlay: clones only the affected data row and cell so PATCH-in-flight UX
 * can show a spinner without touching shared dimensional helpers.
 */
export function applySavingOverlayToPropertyTableRows(
  rows: any[] | undefined,
  saving: { rowId: string | number; columnId: string } | null,
): any[] | undefined {
  if (!saving || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.rowId === 'header' || row.rowId === 'footer') return row;
    if (String(row.rowId) !== String(saving.rowId)) return row;
    if (saving.columnId === 'delete') return row;

    return {
      ...row,
      cells: row.cells.map((cell: any, cellIndex: number) => {
        if (columnIdForCellIndex(cellIndex) !== String(saving.columnId)) return cell;
        const suffix = ` ${SAVING_CLASS}`;
        const nextClass = cell.className
          ? `${String(cell.className).trim()}${suffix}`
          : SAVING_CLASS;
        return { ...cell, className: nextClass };
      }),
    };
  });
}
